import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchPost } from '@app/src/api/fetchHooks'
import { useQueryState } from '@app/src/hooks/queryState'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { QuestionnaireTemplate } from '@app/src/types/resourceExplorer'
import { useMemo } from 'react'
import {
  getScreeningColumns,
  getScreeningTemplateIds,
  toScreeningTemplates,
  type AdverseScreeningQuestionnaire,
} from './findingsUtils'

export type FindingsSlot = { providerCount: number; spendAmount: number; providersWithSpendCount: number }

export type FindingsOverviewModel = {
  withFindings: FindingsSlot
  withoutFindings: FindingsSlot
  notScreened: FindingsSlot
}

export function useHasAnyScreening() {
  const { data, isLoading } = useFetchPost<FindingsOverviewModel, { filter: FilterGroup[] }>({
    key: [FetchKey.FindingsProviders, 'overview', 'has-screening'],
    endpoint: endpoints.findingsOverview,
    payload: { filter: [] },
  })

  const hasAnyScreening = Boolean(data && data.withFindings.providerCount + data.withoutFindings.providerCount > 0)
  return { hasAnyScreening, isLoading }
}

export function useScreeningTemplates() {
  const { items = [], isLoading } = useFetchCollectionWithPost<QuestionnaireTemplate>({
    endpoint: endpoints.adverseScreeningTemplates,
    key: [FetchKey.RequestTemplate, 'findings-screenings'],
    payload: {
      filter: [],
      include: [],
    },
  })

  const screeningTemplates = useMemo(() => toScreeningTemplates(items), [items])
  return { screeningTemplates, isLoading }
}

export const SCREENING_QUERY_PARAM = 'screening'
const SEGMENT_QUERY_PARAM = 'segment'

export enum FindingsSegment {
  Findings = 'findings',
  NoFindings = 'noFindings',
}

const isSegment = (value: unknown): value is FindingsSegment =>
  Object.values(FindingsSegment).includes(value as FindingsSegment)

export function useFindingsView() {
  const { screeningTemplates, isLoading } = useScreeningTemplates()
  const screeningColumns = useMemo(() => getScreeningColumns(screeningTemplates), [screeningTemplates])
  const availableScreenings = useMemo(() => new Set(screeningColumns.map(column => column.type)), [screeningColumns])
  const [rawScreening, setRawScreening] = useQueryState<string | string[] | undefined>(SCREENING_QUERY_PARAM, undefined)
  const screenings = (Array.isArray(rawScreening) ? rawScreening : rawScreening ? [rawScreening] : []).filter(value =>
    availableScreenings.has(value),
  )

  const setScreenings = (values: AdverseScreeningQuestionnaire[]) => setRawScreening(values.length ? values : undefined)

  const [rawSegment, setRawSegment] = useQueryState<string | undefined>(SEGMENT_QUERY_PARAM, undefined)
  const segment = isSegment(rawSegment) ? rawSegment : undefined

  const setSegment = (value: FindingsSegment | undefined) => setRawSegment(value)

  const screeningTemplateIds = useMemo(
    () => screenings.flatMap(type => getScreeningTemplateIds(screeningTemplates, type)),
    [screenings, screeningTemplates],
  )

  const screeningFilters = useMemo<FilterGroup[]>(() => {
    if (!screenings.length || isLoading) return []
    return [
      {
        name: 'questionnaireTemplateId',
        filters: [{ operator: Operators.In, value: screeningTemplateIds.length ? screeningTemplateIds : [-1] }],
      },
    ]
  }, [screenings, screeningTemplateIds, isLoading])

  return {
    screenings,
    setScreenings,
    segment,
    setSegment,
    screeningTemplates,
    screeningColumns,
    screeningTemplateIds,
    screeningFilters,
    isLoading,
  }
}
